"use client";

import { useCallback, useEffect, useRef, useState } from "react";

/** Audio only. Opus-in-WebM where supported (Chrome, Edge, Firefox, newer Safari); AAC-in-MP4 on older Safari. */
export const RECORDER_MIME_TYPES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"] as const;
/** MediaRecorder hands a chunk over every second, so a crash loses at most that much of what is already in memory. */
export const RECORDER_TIMESLICE_MS = 1000;
const TICK_MS = 100;

export type RecorderSource = "mic" | "micAndTab";
export type RecorderStatus = "idle" | "starting" | "recording" | "paused" | "stopping";
export type RecorderErrorCode =
  | "UNSUPPORTED" | "TAB_UNSUPPORTED" | "PERMISSION_DENIED" | "NO_DEVICE" | "DEVICE_BUSY" | "SHARE_CANCELLED" | "NO_TAB_AUDIO" | "FAILED";

export class RecorderError extends Error {
  constructor(public code: RecorderErrorCode) {
    super(code);
    this.name = "RecorderError";
  }
}

export interface Recording { blob: Blob; mimeType: string; extension: "webm" | "m4a" | "ogg"; durationS: number; startedAt: Date }

export function pickMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  return RECORDER_MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? null;
}

export function extensionFor(mimeType: string): Recording["extension"] {
  if (mimeType.startsWith("audio/mp4")) return "m4a";
  if (mimeType.startsWith("audio/ogg")) return "ogg";
  return "webm";
}

/** What this browser can record: the microphone, and a shared tab/window's audio (desktop Chrome/Edge; not mobile). */
export function recorderSupport(): { mic: boolean; tab: boolean } {
  const md = typeof navigator === "undefined" ? undefined : navigator.mediaDevices;
  const mic = typeof md?.getUserMedia === "function" && typeof AudioContext !== "undefined" && pickMimeType() !== null;
  return { mic, tab: mic && typeof md?.getDisplayMedia === "function" };
}

function mediaError(err: unknown, display: boolean): RecorderError {
  switch (err instanceof DOMException || err instanceof Error ? err.name : undefined) {
    case "NotAllowedError":
    case "SecurityError": return new RecorderError(display ? "SHARE_CANCELLED" : "PERMISSION_DENIED");   // Chrome reports a dismissed share picker as NotAllowedError
    case "NotFoundError":
    case "OverconstrainedError": return new RecorderError("NO_DEVICE");
    case "NotReadableError":
    case "AbortError": return new RecorderError("DEVICE_BUSY");
    default: return new RecorderError("FAILED");
  }
}

/** RMS of the analyser's current window, scaled so ordinary speech fills most of a meter (speech RMS rarely passes ~0.3). */
function levelOf(analyser: AnalyserNode, buf: Uint8Array<ArrayBuffer>): number {
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (const v of buf) {
    const x = (v - 128) / 128;
    sum += x * x;
  }
  return Math.min(1, Math.sqrt(sum / buf.length) * 3);
}

interface MicInput {
  stream: MediaStream;
  node: MediaStreamAudioSourceNode;
}

interface Session {
  mic: MicInput;
  streams: MediaStream[];   // everything else captured (the shared tab)
  ctx: AudioContext;
  dest: MediaStreamAudioDestinationNode;
  analyser: AnalyserNode;
  switching: number;   // bumps per switch, so only the latest pick is kept when several race
  recorder: MediaRecorder;
  chunks: Blob[];
  mimeType: string;
  startedAt: Date;
  accumulatedMs: number;
  runningSince: number | null;   // Date.now() of the current un-paused stretch; null while paused
  timer: number;
}

const elapsedOf = (s: Session) => s.accumulatedMs + (s.runningSince === null ? 0 : Date.now() - s.runningSince);
const stopTracks = (streams: MediaStream[]) => streams.forEach((st) => st.getTracks().forEach((t) => t.stop()));

function micConstraints(deviceId: string): MediaTrackConstraints {
  const audio: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true };
  if (deviceId) audio.deviceId = { exact: deviceId };   // an unplugged pick fails as NO_DEVICE rather than silently recording another mic
  return audio;
}

type SessionRef = { current: Session | null };

/** A mic unplugged mid-recording would leave the file silent from then on, so its end falls back to the default mic. */
function watchMic(s: Session, current: SessionRef, onError: (e: RecorderError | null) => void) {
  for (const t of s.mic.stream.getAudioTracks()) {
    t.onended = () => { if (current.current === s) void swapMic(s, "", current, onError); };
  }
}

/**
 * Swaps the recorded microphone without interrupting the recording: the new mic joins the mix before the old one
 * leaves it, so the file has no gap. If the new mic can't be opened the old one keeps recording and `onError` says
 * why. `deviceId` empty is the system default.
 */
async function swapMic(s: Session, deviceId: string, current: SessionRef, onError: (e: RecorderError | null) => void) {
  const token = ++s.switching;
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(deviceId) });
  } catch (e) {
    if (current.current === s && s.switching === token) onError(mediaError(e, false));
    return;
  }
  if (current.current !== s || s.switching !== token) { stopTracks([stream]); return; }   // stopped, or a later pick won
  const node = s.ctx.createMediaStreamSource(stream);
  node.connect(s.dest);
  node.connect(s.analyser);
  const old = s.mic;
  s.mic = { stream, node };
  watchMic(s, current, onError);
  old.node.disconnect();
  old.stream.getTracks().forEach((t) => { t.onended = null; t.stop(); });
  onError(null);
}

/**
 * In-browser audio capture: the microphone, optionally mixed (Web Audio) with a shared tab/window's audio, recorded
 * by one MediaRecorder. Elapsed time excludes pauses. Every track and the AudioContext are released on stop, discard,
 * failure and unmount, so the browser's recording indicator never outlives the recording.
 */
export function useBrowserRecorder() {
  const [status, setStatus] = useState<RecorderStatus>("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<RecorderError | null>(null);
  const session = useRef<Session | null>(null);
  const alive = useRef(true);

  const release = useCallback(() => {
    const s = session.current;
    session.current = null;
    if (!s) return;
    clearInterval(s.timer);
    s.mic.stream.getTracks().forEach((t) => { t.onended = null; });
    s.recorder.ondataavailable = null;
    s.recorder.onstop = null;
    s.recorder.onerror = null;
    if (s.recorder.state !== "inactive") s.recorder.stop();
    stopTracks([s.mic.stream, ...s.streams]);
    void s.ctx.close();
  }, []);

  const switchMicrophone = useCallback(
    (deviceId: string) => (session.current ? swapMic(session.current, deviceId, session, setError) : Promise.resolve()),
    [],
  );


  const reset = useCallback(() => {
    setStatus("idle");
    setElapsedMs(0);
    setLevel(0);
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      release();
    };
  }, [release]);

  /** `deviceId` — a mic from `useMicrophones`; empty is the system default. */
  const start = useCallback(async (source: RecorderSource, deviceId = "") => {
    if (session.current) return;
    setError(null);
    const md = typeof navigator === "undefined" ? undefined : navigator.mediaDevices;
    const mimeType = pickMimeType();
    const support = recorderSupport();
    if (!support.mic || !md || !mimeType) { setError(new RecorderError("UNSUPPORTED")); return; }
    if (source === "micAndTab" && !support.tab) { setError(new RecorderError("TAB_UNSUPPORTED")); return; }
    setStatus("starting");
    const streams: MediaStream[] = [];
    let ctx: AudioContext | null = null;
    try {
      const micStream = await md.getUserMedia({ audio: micConstraints(deviceId) }).catch((e) => { throw mediaError(e, false); });
      streams.push(micStream);
      if (source === "micAndTab") {
        // Chrome only offers tab/system audio alongside video; the video track is dropped at once and never recorded.
        const display = await md.getDisplayMedia({ video: true, audio: true }).catch((e) => { throw mediaError(e, true); });
        streams.push(display);
        for (const t of display.getVideoTracks()) { t.stop(); display.removeTrack(t); }
        if (display.getAudioTracks().length === 0) throw new RecorderError("NO_TAB_AUDIO");
      }
      if (!alive.current) { stopTracks(streams); return; }

      ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      const nodes = streams.map((st) => {
        const node = ctx!.createMediaStreamSource(st);
        node.connect(dest);
        node.connect(analyser);
        return node;
      });
      void ctx.resume();   // a context created after an awaited permission prompt may start suspended
      const recorder = new MediaRecorder(dest.stream, { mimeType });
      const buf = new Uint8Array(analyser.fftSize);
      const s: Session = {
        mic: { stream: micStream, node: nodes[0] }, streams: streams.slice(1), ctx, dest, analyser, switching: 0,
        recorder, chunks: [], mimeType, startedAt: new Date(), accumulatedMs: 0, runningSince: Date.now(),
        timer: window.setInterval(() => {
          setElapsedMs(elapsedOf(s));
          setLevel(s.runningSince === null ? 0 : levelOf(analyser, buf));
        }, TICK_MS),
      };
      recorder.ondataavailable = (e) => { if (e.data.size > 0) s.chunks.push(e.data); };
      recorder.onerror = () => {
        release();
        reset();
        setError(new RecorderError("FAILED"));
      };
      session.current = s;
      watchMic(s, session, setError);
      recorder.start(RECORDER_TIMESLICE_MS);
      setElapsedMs(0);
      setStatus("recording");
    } catch (err) {
      if (session.current) release();
      else { stopTracks(streams); void ctx?.close(); }
      reset();
      setError(err instanceof RecorderError ? err : mediaError(err, false));
    }
  }, [release, reset]);

  const pause = useCallback(() => {
    const s = session.current;
    if (!s || s.recorder.state !== "recording") return;
    s.recorder.pause();
    s.accumulatedMs = elapsedOf(s);
    s.runningSince = null;
    setElapsedMs(s.accumulatedMs);
    setLevel(0);
    setStatus("paused");
  }, []);

  const resume = useCallback(() => {
    const s = session.current;
    if (!s || s.recorder.state !== "paused") return;
    s.recorder.resume();
    s.runningSince = Date.now();
    setStatus("recording");
  }, []);

  /** Finalises the recording (the last chunk arrives before `stop`) and releases the devices. Null when nothing is recording. */
  const stop = useCallback((): Promise<Recording | null> => {
    const s = session.current;
    if (!s) return Promise.resolve(null);
    const durationMs = elapsedOf(s);
    s.accumulatedMs = durationMs;
    s.runningSince = null;
    setStatus("stopping");
    return new Promise((resolve) => {
      s.recorder.onstop = () => {
        const blob = new Blob(s.chunks, { type: s.mimeType });
        release();
        reset();
        resolve({ blob, mimeType: s.mimeType, extension: extensionFor(s.mimeType), durationS: Math.round(durationMs / 1000), startedAt: s.startedAt });
      };
      s.recorder.stop();
    });
  }, [release, reset]);

  const discard = useCallback(() => {
    release();
    reset();
  }, [release, reset]);

  return { status, elapsedMs, level, error, start, pause, resume, stop, discard, switchMicrophone };
}
